import React from 'react';
import Reveal from '../components/Reveal';
import {
  appSections,
  deviceSupport,
  featureGroups,
  featureList,
  homeHighlights,
  iosDetails,
  premiumFeatures,
  workflow
} from '../content/siteContent';

const APP_STORE_URL = 'https://apps.apple.com/tr/app/vizo-music-visualizer/id6749325448';

const Home: React.FC = () => {
  return (
    <div className="pb-20">
      {/* Hero */}
      <section className="apple-hero px-6 pb-16 pt-20 md:pt-32">
        <div className="hero-glow" />
        <img
          src="/vizo-logo.png"
          alt="Vizo"
          className="hero-icon mx-auto mb-7 h-20 w-20 rounded-[22px] md:h-24 md:w-24"
        />
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-white/50">Vizo</p>
        <h1 className="mx-auto max-w-4xl text-white">
          <span className="shimmer-text">Music visualizer.</span>
          <br />
          <span className="text-white/40">Reimagined for iPhone.</span>
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/60 md:text-xl">
          Real-time audio spectrum visuals, templates, presets, and clean exports —
          all in one beautifully designed app.
        </p>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="apple-button glow">
            Download on the App Store
          </a>
          <a href="/support" className="apple-button-outline">
            Learn more
          </a>
        </div>
        <div className="eq-bars mx-auto mt-12 w-full max-w-[200px]" aria-hidden="true">
          <span /><span /><span /><span /><span /><span /><span />
        </div>
      </section>

      {/* Screenshots */}
      <section className="px-6 pb-16">
        <div className="mx-auto max-w-5xl">
          <Reveal className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
            {['/screenshot-1.jpg', '/screenshot-2.jpg', '/screenshot-3.jpg', '/screenshot-4.jpg', '/screenshot-5.jpg'].map((src, i) => (
              <img
                key={src}
                src={src}
                alt={`Vizo screenshot ${i + 1}`}
                className="w-full rounded-2xl border border-white/10 object-cover transition duration-500 hover:scale-[1.03] hover:border-white/20"
              />
            ))}
          </Reveal>
        </div>
      </section>

      {/* Highlights */}
      <section className="px-6 py-16">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-12 text-center text-4xl font-semibold tracking-tight text-white md:text-5xl">
              Built for creators.
            </h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2">
            {homeHighlights.map((item, i) => (
              <Reveal key={item.title} delay={i * 100}>
                <div className="apple-card">
                  <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-16">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-4 text-center text-4xl font-semibold tracking-tight text-white md:text-5xl">
              Everything included.
            </h2>
            <p className="mx-auto mb-12 max-w-2xl text-center text-lg text-white/50">
              All core Vizo features, clearly listed.
            </p>
          </Reveal>
          <div className="grid gap-3 md:grid-cols-2">
            {featureList.map((feature, i) => (
              <Reveal key={feature} delay={(i % 2) * 80}>
                <div className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 transition duration-500 hover:border-white/20 hover:bg-white/[0.05]">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                  <span className="text-sm leading-relaxed text-white/70">{feature}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Groups */}
      <section className="px-6 py-16">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-12 text-center text-4xl font-semibold tracking-tight text-white md:text-5xl">
              A closer look.
            </h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2">
            {featureGroups.map((group, i) => (
              <Reveal key={group.title} delay={i * 100}>
                <div className="apple-card">
                  <h3 className="text-2xl font-semibold text-white">{group.title}</h3>
                  <p className="mt-2 text-sm text-white/50">{group.description}</p>
                  <ul className="mt-5 space-y-2.5">
                    {group.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="px-6 py-16">
        <div className="apple-section">
          <div className="grid gap-4 lg:grid-cols-2">
            <Reveal>
              <div className="apple-card">
                <h2 className="text-3xl font-semibold tracking-tight text-white">How it works.</h2>
                <ol className="mt-6 space-y-4">
                  {workflow.map((step, i) => (
                    <li key={step} className="flex gap-4">
                      <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-white">{i + 1}</span>
                      <span className="pt-0.5 text-sm leading-relaxed text-white/70">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="apple-card">
                <h2 className="text-3xl font-semibold tracking-tight text-white">Vizo Pro.</h2>
                <p className="mt-3 text-sm text-white/50">Unlock premium tools when you need more control.</p>
                <ul className="mt-5 space-y-2.5">
                  {premiumFeatures.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-white/70">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <a href="/support" className="apple-link mt-6 inline-block text-sm font-semibold">
                  Learn about support and subscriptions &rsaquo;
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* App Sections */}
      <section className="px-6 py-16">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-12 text-center text-4xl font-semibold tracking-tight text-white md:text-5xl">
              The full experience.
            </h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {appSections.map((section, index) => (
              <Reveal key={section.title} delay={(index % 3) * 100}>
                <div className={`apple-card ${index === 0 ? 'xl:col-span-2' : ''}`}>
                  <h3 className="text-2xl font-semibold text-white">{section.title}</h3>
                  <ul className="mt-5 space-y-2.5">
                    {section.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Device Support */}
      <section className="px-6 py-16">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-4 text-center text-4xl font-semibold tracking-tight text-white md:text-5xl">
              Made for every Apple device.
            </h2>
            <p className="mx-auto mb-12 max-w-2xl text-center text-lg text-white/50">
              Vizo runs natively on iPhone, iPad, Mac with Apple Silicon, and Apple Vision Pro.
            </p>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {deviceSupport.map((device, i) => (
              <Reveal key={device.title} delay={i * 100}>
                <div className="apple-card flex flex-col items-center text-center">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.06] text-2xl">
                    {device.icon === 'iPhone' && '📱'}
                    {device.icon === 'iPad' && '📲'}
                    {device.icon === 'Mac' && '💻'}
                    {device.icon === 'Vision' && '🥽'}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{device.title}</h3>
                  <p className="mt-2 text-sm text-white/50">{device.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Details */}
      <section className="px-6 py-16">
        <div className="apple-section">
          <div className="grid gap-4 lg:grid-cols-2">
            <Reveal>
              <div className="apple-card">
                <h2 className="text-3xl font-semibold tracking-tight text-white">iPhone and iOS.</h2>
                <p className="mt-3 text-sm text-white/50">
                  Clear platform details for users and App Store visitors.
                </p>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="apple-card">
                <div className="grid gap-3 md:grid-cols-2">
                  {iosDetails.map((detail) => (
                    <div key={detail} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/70">
                      {detail}
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20 text-center">
        <Reveal>
          <h2 className="mx-auto max-w-3xl text-4xl font-semibold tracking-tight text-white md:text-6xl">
            Ready to visualize?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-lg text-white/50">
            Download Vizo and start creating music visuals today.
          </p>
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="apple-button mt-8">
            Download on the App Store
          </a>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
